import { useEffect } from 'react'

import { CEREBRO_ACCESS_TOKEN, CEREBRO_ID_TOKEN } from 'const/localStorage'
import { readJwtUserId } from 'features/Auth/jwtClaims'
import { signOutLocally } from 'features/Auth/service/signOut'

interface CrossTabSessionSyncProps {
    reload?: () => void
}

const reloadPage = (): void => window.location.reload()

// Events arrive after the fact, so a removal only counts if the session is
// still gone: this tab may have signed in again since. A whole-storage clear
// (key === null) is judged the same way.
function sessionEndedInAnotherTab(event: StorageEvent): boolean {
    const removedAccessToken =
        event.key === null ||
        (event.key === CEREBRO_ACCESS_TOKEN && event.newValue === null)
    return (
        removedAccessToken &&
        localStorage.getItem(CEREBRO_ACCESS_TOKEN) === null
    )
}

// A rotated id token for the same user is routine (silent refresh); every
// request already reads the current tokens from storage, so nothing to do.
function userChangedInAnotherTab(event: StorageEvent): boolean {
    return (
        event.key === CEREBRO_ID_TOKEN &&
        event.newValue !== null &&
        readJwtUserId(event.oldValue) !== readJwtUserId(event.newValue)
    )
}

export function CrossTabSessionSync({
    reload = reloadPage,
}: CrossTabSessionSyncProps): null {
    useEffect(() => {
        const handleStorageEvent = (event: StorageEvent): void => {
            if (sessionEndedInAnotherTab(event)) {
                void signOutLocally()
                return
            }
            if (userChangedInAnotherTab(event)) {
                reload()
            }
        }
        window.addEventListener('storage', handleStorageEvent)
        return () => {
            window.removeEventListener('storage', handleStorageEvent)
        }
    }, [reload])

    return null
}
